import type { AxiosError } from 'axios';
import HttpMethod from '../../request/HttpMethod';
import HttpStatusCode from '../../request/HttpStatusCode';
import Requester from '../../request/Requester';
import type { Configuration, IRequest } from '../../request/Types';

const AVATAR_ENDPOINT = '/account/avatar';

/**
 * Foto de perfil do usuário logado (herbie-api PR #1448). O usuário nunca vai no payload:
 * o backend deriva o id da sessão (header X-Session) e guarda o objeto em
 * `users/{user_id}/avatar.png`. Mesma estratégia de storage do logo da frota
 * (`FleetRequester`), com o corpo da requisição sendo os bytes crus do PNG.
 */
export class AvatarRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public uploadAvatar = (blob: Blob): Promise<string> => {
    const requestData: IRequest = {
      endpoint: AVATAR_ENDPOINT,
      method: HttpMethod.POST,
      body: blob,
      headers: {
        'Content-type': 'application/octet-stream',
      },
    };

    return this.requester.request(requestData).then((response) => response.data.avatar_url);
  };

  /**
   * 404 é resposta esperada de quem nunca subiu foto — resolve `null` em vez de rejeitar,
   * e sem reportar ao Datadog.
   */
  public getAvatarUrl = (): Promise<string | null> => {
    const requestData: IRequest = {
      endpoint: AVATAR_ENDPOINT,
      method: HttpMethod.GET,
    };

    return this.requester
      .request(requestData, [HttpStatusCode.NOT_FOUND])
      .then((response) => response.data.avatar_url ?? null)
      .catch((error: AxiosError) => {
        if (error.response?.status === HttpStatusCode.NOT_FOUND) return null;
        throw error;
      });
  };

  public deleteAvatar = (): Promise<null> => {
    const requestData: IRequest = {
      endpoint: AVATAR_ENDPOINT,
      method: HttpMethod.DELETE,
    };

    return this.requester.request(requestData).then(() => null);
  };
}

export default AvatarRequester;
